import type { ImageAttemptDisplayStatus, ImageContentMode, ImageFinalDecision, ImageHistoryOutcome, ImageModificationStageView, ImageRequestedChanges, ImageSlotView, MembershipRole } from "../../../api/types.js";

type AnyDisplayStatus = ImageAttemptDisplayStatus | "retrying" | "exhausted" | "needs_reconciliation";

/** Same ladder as the server (`ROLE_RANK` in `authorize.ts`): VIEWER < REVIEWER < OPERATOR < ADMIN. */
const ROLE_RANK: Record<MembershipRole, number> = { VIEWER: 0, REVIEWER: 1, OPERATOR: 2, ADMIN: 3 };

export function roleAtLeast(role: MembershipRole | null, minimum: MembershipRole): boolean {
  return role !== null && ROLE_RANK[role] >= ROLE_RANK[minimum];
}

/**
 * The role that governs what this user may DO in Image Mode. A platform
 * OWNER is unrestricted server-side; a member's role comes from their
 * membership in the selected workspace. The role decides which Image ACTIONS
 * are enabled — never whether Image Mode exists for the user.
 */
export function effectiveImageRole(input: { readonly isPlatformOwner: boolean; readonly memberships: readonly { readonly tenantId: string; readonly role: MembershipRole }[]; readonly tenantId: string }): MembershipRole | null {
  if (input.isPlatformOwner) {
    return "ADMIN";
  }
  return input.memberships.find((m) => m.tenantId === input.tenantId)?.role ?? null;
}

/** Upload / Generate / (manual) retry. */
export const OPERATOR_ACTION_ROLE: MembershipRole = "OPERATOR";
/** Approve / reject an image and Create Bundle. */
export const REVIEWER_ACTION_ROLE: MembershipRole = "REVIEWER";

export type ImageModeStep = "loading" | "verify_email" | "workspace" | "persona" | "ready";

/**
 * Image Mode's setup gate — deliberately just the account/persona
 * preconditions (verified email, a workspace, a Generate-ready persona). It
 * has NO license/plan step and NO Image-specific switch: whether the
 * workspace may spend right now is reported inside the Image form
 * (`ImageModeConfig.access`), never by hiding Image Mode.
 */
export function resolveImageModeStep(input: {
  readonly isPlatformOwner: boolean;
  readonly emailVerified: boolean;
  readonly tenantId: string | null;
  /** The server-filtered list (`GET /images/:tenantId/personas`): only personas this user may use that are Generate-ready. */
  readonly personas: readonly { readonly personaId: string }[] | null;
}): ImageModeStep {
  if (!input.isPlatformOwner && !input.emailVerified) {
    return "verify_email";
  }
  if (input.tenantId === null) {
    return "workspace";
  }
  if (input.personas === null) {
    return "loading";
  }
  if (input.personas.length === 0) {
    return "persona";
  }
  return "ready";
}

export const DISPLAY_STATUS_LABEL: Record<AnyDisplayStatus, string> = {
  waiting: "Waiting",
  generating: "Generating…",
  verifying: "Verifying…",
  retrying: "Retrying with a new variation…",
  ready_for_review: "Ready for review",
  approved: "Approved",
  rejected: "Rejected",
  generation_failed: "Generation failed",
  generation_uncertain: "Generation uncertain",
  content_moderation_rejected: "Content moderation rejected",
  identity_failed: "Identity check failed",
  identity_inconclusive: "Identity could not be verified",
  too_similar: "Too similar to another image",
  distinctness_inconclusive: "Similarity check inconclusive",
  spend_limit_stopped: "Stopped: spend limit reached",
  preservation_failed: "Did not preserve the original",
  verification_reconciliation: "Could not be verified - needs review",
  modification_stopped: "Requested change failed",
  modification_reconciliation: "Requested change could not be verified - needs review",
  modifying: "Applying requested changes…",
  needs_reconciliation: "Outcome unconfirmed - needs review",
  exhausted: "Could not be produced",
};

export type BadgeTone = "good" | "progress" | "bad";

export function displayStatusTone(status: AnyDisplayStatus): BadgeTone {
  switch (status) {
    case "ready_for_review":
    case "approved":
      return "good";
    case "waiting":
    case "generating":
    case "verifying":
    case "modifying":
    case "retrying":
      return "progress";
    default:
      return "bad";
  }
}

/** Whether the slot's current image counts as a valid delivered image (the only kind that may be approved or bundled). */
export function isValidSlot(slot: ImageSlotView): boolean {
  return slot.status === "valid";
}

/** Plain-language reason a non-valid attempt does not count — shown on the card so nobody needs the logs. */
export function notCountedExplanation(status: AnyDisplayStatus): string | null {
  switch (status) {
    case "identity_failed":
      return "Not counted: this image did not pass the identity check.";
    case "identity_inconclusive":
      return "Not counted: identity could not be verified for this image.";
    case "too_similar":
      return "Not counted: too similar to an image that was already accepted.";
    case "distinctness_inconclusive":
      return "Not counted: the similarity check could not be completed.";
    case "content_moderation_rejected":
      return "Not counted: the provider's content filter blocked this generation.";
    case "generation_failed":
      return "Not counted: the generation failed.";
    case "generation_uncertain":
    case "needs_reconciliation":
      return "Not counted: the generation outcome could not be confirmed and it may already have been billed. It is NOT retried automatically - check your provider billing, then use Retry if you want another attempt.";
    case "spend_limit_stopped":
      return "Stopped: the workspace's monthly provider spend limit was reached, so no further paid call was made for this image.";
    default:
      return null;
  }
}

/** The whole Bundle as ONE archive (server-built .zip) - no image-by-image downloading. */
export function bundleDownloadHref(tenantId: string, bundleId: string): string {
  return `/images/${encodeURIComponent(tenantId)}/bundles/${encodeURIComponent(bundleId)}/download`;
}

/** Up to 4 decimals (so a $0.0055 verification call is never rounded away), trailing zeros trimmed down to a minimum of 2 ("$0.09", "$1.35", "$0.0055", "$0.00"). */
export function formatUsd(value: number): string {
  const [whole, fraction = ""] = value.toFixed(4).split(".");
  let trimmed = fraction;
  while (trimmed.length > 2 && trimmed.endsWith("0")) {
    trimmed = trimmed.slice(0, -1);
  }
  return `$${whole}.${trimmed}`;
}

/** The pre-Generate label: requested count x the server-provided per-image generation price. Always "Estimated generation cost" — never a total. */
export function estimatedGenerationCostUsd(count: number, pricePerImageUsd: number): number {
  return Math.round(count * pricePerImageUsd * 100) / 100;
}

export const CONTENT_MODE_LABEL: Record<ImageContentMode, string> = { sfw: "SFW", spicy: "SPICY", nsfw: "NSFW" };

/** The three Requested Changes, exactly as the user selected them (nothing is pre-selected). */
export interface RequestedChangeSelection {
  readonly clothingColorEnabled: boolean;
  readonly clothingGarment: string;
  readonly clothingColor: string;
  readonly pendantEnabled: boolean;
  /** The chosen pendant preset's prompt (from the server-provided registry). */
  readonly pendantPrompt: string;
  readonly glassesEnabled: boolean;
  /** The chosen glasses preset's prompt. */
  readonly glassesPrompt: string;
}

export const NO_REQUESTED_CHANGES: RequestedChangeSelection = {
  clothingColorEnabled: false,
  clothingGarment: "",
  clothingColor: "",
  pendantEnabled: false,
  pendantPrompt: "",
  glassesEnabled: false,
  glassesPrompt: "",
};

/**
 * The `requestedChanges` sent with Generate: ONLY what the user selected — an unselected change is never present in the request, so it can
 * never be executed. Returns `null` when nothing is selected.
 */
export function buildRequestedChangesBody(selection: RequestedChangeSelection): ImageRequestedChanges | null {
  const body: {
    clothingColor?: { enabled: true; targetGarment?: string; targetColor: string };
    pendant?: { enabled: true; prompt?: string };
    referenceModifications?: Record<string, { enabled: true; prompt?: string }>;
  } = {};
  if (selection.clothingColorEnabled) {
    const garment = selection.clothingGarment.trim();
    body.clothingColor = { enabled: true, targetColor: selection.clothingColor.trim(), ...(garment.length > 0 ? { targetGarment: garment } : {}) };
  }
  if (selection.pendantEnabled) {
    body.pendant = { enabled: true, ...(selection.pendantPrompt.length > 0 ? { prompt: selection.pendantPrompt } : {}) };
  }
  if (selection.glassesEnabled) {
    body.referenceModifications = { glasses: { enabled: true, ...(selection.glassesPrompt.length > 0 ? { prompt: selection.glassesPrompt } : {}) } };
  }
  return Object.keys(body).length === 0 ? null : body;
}

/** The Requested-Change stages that did NOT apply their change (rejected, failed, or an unknown outcome) — shown on a delivered image so it is never mistaken for the edited version. */
export function unappliedStages(stages: readonly ImageModificationStageView[] | undefined): readonly ImageModificationStageView[] {
  return (stages ?? []).filter((stage) => stage.status === "rejected" || stage.status === "failed" || stage.status === "uncertain");
}

/** The outcome label of a request that lives in HISTORY (Approved / Rejected, or why there is no decision). */
export function historyOutcomeLabel(request: { readonly historyOutcome?: ImageHistoryOutcome | null; readonly finalDecision: ImageFinalDecision | null }): string {
  switch (request.historyOutcome ?? request.finalDecision) {
    case "approved":
      return "Approved";
    case "rejected":
      return "Rejected";
    case "no_image_delivered":
      return "No image delivered";
    case "no_decision_recorded":
      return "No decision recorded";
    default:
      return "—";
  }
}

const STAGE_LABEL: Record<string, string> = { pendant_removal: "Pendant removal", pendant: "Pendant", glasses: "Glasses", clothing_color: "Clothing color" };

export function stageLabel(targetId: string): string {
  return STAGE_LABEL[targetId] ?? targetId;
}

/** One line naming exactly what the user selected before Generate ("none" when nothing). */
export function describeRequestedChanges(changes: ImageRequestedChanges | null | undefined): string {
  if (changes === null || changes === undefined) {
    return "none";
  }
  const parts: string[] = [];
  if (changes.clothingColor?.enabled === true) {
    parts.push(`clothing color${changes.clothingColor.targetGarment ? ` (${changes.clothingColor.targetGarment})` : ""} → ${changes.clothingColor.targetColor ?? "?"}`);
  }
  if (changes.pendant?.enabled === true) {
    parts.push(`pendant${changes.pendant.prompt ? ` (${changes.pendant.prompt})` : ""}`);
  }
  if (changes.referenceModifications?.glasses?.enabled === true) {
    parts.push("glasses");
  }
  return parts.length === 0 ? "none" : parts.join("; ");
}

/** A selected clothing-colour change needs a colour (the same rule Video Mode enforces server-side). */
export function requestedChangesProblem(selection: RequestedChangeSelection): string | null {
  return selection.clothingColorEnabled && selection.clothingColor.trim().length === 0 ? "Enter the colour for the clothing colour change (or turn it off)" : null;
}

export function clampRequestedCount(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.min(max, Math.max(min, Math.round(value)));
}
